/**
 * <mango-calendar> — a month grid of the provider's scheduled pickups.
 * =====================================================================
 * Built from the work-order list (each order's dropoff_datetime). Each pickup is
 * a status-coloured chip on its day; clicking a chip opens that work order's
 * stage-appropriate detail screen. Prev/Next step the month. This is a read view
 * over scheduled pickups — there is no availability/blocking model yet.
 *
 * On mobile (≤768px) a vertical agenda list replaces the 7-column grid.  Both
 * surfaces are rendered from the same `this.orders` array; CSS toggles which is
 * visible.  The agenda shows upcoming pickups (today-or-future), sorted
 * chronologically, using the same data fields and detailRoute the grid uses.
 *
 * Color legend: renderLegend() builds one swatch per ProviderStatus using the
 * same STATUS_CONFIG pill classes already on the chips — no invented colors.
 *
 * Reschedule modal: wireReschedule() handles open/close, populates the work-order
 * select, updates the "Currently scheduled" line on change, and POSTs to the
 * reschedule endpoint (POST /api/v1/provider/work-orders/{short_name}/reschedule/)
 * with body { requested_for: "<ISO 8601 local datetime>" }. On success the order
 * in this.orders has its reschedule_requested_for set and the grid + agenda re-render
 * to show the "Reschedule pending" indicator.
 *
 * Reschedule-pending: a job whose reschedule_requested_for is non-null gets a
 * "(Rescheduling…)" suffix on its grid chip and the pill-amber "Reschedule pending"
 * badge on its agenda row.
 *
 * fmv_2 NEVER appears. No data-bind here (the screen maps over many work orders).
 */
import { listWorkOrders, type WorkOrder } from "../api/client";
import { request } from "../api/http";
import { loadView, STATUS_CONFIG, PIPELINE, detailRoute, date, type StatusVisual } from "./shared";

const VIEW_URL = new URL("../../views/calendar.html", import.meta.url).href;

export class MangoCalendar extends HTMLElement {
  private orders: WorkOrder[] = [];
  private viewYear = 0;
  private viewMonth = 0; // 0–11

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);
    const now = new Date();
    this.viewYear = now.getFullYear();
    this.viewMonth = now.getMonth();
    this.wire();
    this.wireReschedule();
    await this.load();
  }

  private setStatus(message: string): void {
    const el = this.querySelector<HTMLElement>("[data-status]");
    if (el) el.textContent = message;
  }

  private wire(): void {
    this.querySelector<HTMLButtonElement>("[data-cal-prev]")?.addEventListener("click", () => this.step(-1));
    this.querySelector<HTMLButtonElement>("[data-cal-next]")?.addEventListener("click", () => this.step(1));
    // Chip / agenda-row → open the work order (click + Enter/Space, CSP-safe).
    const go = (target: HTMLElement): void => {
      const link = target.closest<HTMLElement>("[data-cal-link]");
      const id = link?.dataset.id;
      const route = link?.dataset.route;
      if (id && route) window.location.hash = `#/${route}/${encodeURIComponent(id)}`;
    };
    this.addEventListener("click", (e) => go(e.target as HTMLElement));
    this.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (!(e.target as HTMLElement).closest("[data-cal-link]")) return;
      e.preventDefault();
      go(e.target as HTMLElement);
    });
  }

  private step(delta: number): void {
    this.viewMonth += delta;
    if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear -= 1; }
    else if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear += 1; }
    this.render();
  }

  private async load(): Promise<void> {
    this.setStatus("Loading calendar…");
    try {
      const page = await listWorkOrders({ limit: 100 });
      this.orders = page.results;
    } catch (error) {
      console.error("[mango-calendar] failed to load work orders", error);
      this.setStatus("Could not load the calendar.");
      return;
    }
    this.render();
  }

  /** Group the viewed month's pickups by day-of-month (parsed from the ISO date
   *  string's leading YYYY-MM-DD to avoid timezone drift). */
  private pickupsByDay(): Map<number, WorkOrder[]> {
    const byDay = new Map<number, WorkOrder[]>();
    for (const job of this.orders) {
      const iso = (job.dropoff_datetime ?? "").slice(0, 10); // YYYY-MM-DD
      if (iso.length !== 10) continue;
      const parts = iso.split("-");
      const y = Number.parseInt(parts[0] ?? "", 10);
      const m = Number.parseInt(parts[1] ?? "", 10);
      const d = Number.parseInt(parts[2] ?? "", 10);
      if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) continue;
      if (y !== this.viewYear || m - 1 !== this.viewMonth) continue;
      const arr = byDay.get(d) ?? [];
      arr.push(job);
      byDay.set(d, arr);
    }
    return byDay;
  }

  /**
   * Parse YYYY-MM-DD from `dropoff_datetime` without timezone drift.
   * Returns null if the field is absent or unparseable.
   */
  private parseIsoDate(job: WorkOrder): { y: number; m: number; d: number } | null {
    const iso = (job.dropoff_datetime ?? "").slice(0, 10);
    if (iso.length !== 10) return null;
    const parts = iso.split("-");
    const y = Number.parseInt(parts[0] ?? "", 10);
    const m = Number.parseInt(parts[1] ?? "", 10);
    const d = Number.parseInt(parts[2] ?? "", 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
    return { y, m, d };
  }

  /**
   * Return orders with a dropoff_datetime on or after today, sorted ascending.
   * Uses wall-clock date comparison (YYYY-MM-DD string prefix) so no tz drift.
   */
  private upcomingOrders(): WorkOrder[] {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return this.orders
      .filter((job) => {
        const iso = (job.dropoff_datetime ?? "").slice(0, 10);
        return iso.length === 10 && iso >= todayStr;
      })
      .sort((a, b) => {
        const ia = (a.dropoff_datetime ?? "").slice(0, 10);
        const ib = (b.dropoff_datetime ?? "").slice(0, 10);
        return ia < ib ? -1 : ia > ib ? 1 : 0;
      });
  }

  private render(): void {
    this.renderLegend();
    this.renderGrid();
    this.renderAgenda();
  }

  // ── Color legend ───────────────────────────────────────────────────────────
  /**
   * Render the status-color legend beneath the topbar. One pill swatch per
   * ProviderStatus, using the same STATUS_CONFIG pillClass the grid chips use.
   * The legend label prefix ("Legend:") is authored in calendar.html; this method
   * appends the swatches only, so it never duplicates the label on re-renders.
   */
  private renderLegend(): void {
    const container = this.querySelector<HTMLElement>("[data-cal-legend]");
    if (!container) return;
    // Keep the authored "Legend:" label span; remove any previously appended swatches.
    const swatches = container.querySelectorAll("[data-cal-legend-swatch]");
    swatches.forEach((s) => s.remove());

    for (const status of PIPELINE) {
      const cfg: StatusVisual | undefined = STATUS_CONFIG[status];
      if (!cfg) continue;
      const swatch = document.createElement("span");
      swatch.setAttribute("data-cal-legend-swatch", "");
      swatch.className = `pill ${cfg.pillClass}`;
      swatch.style.cssText = "font-size:10px;display:inline-flex;align-items:center;gap:4px";
      swatch.innerHTML = `<i class="ti ${cfg.icon}" style="font-size:10px"></i> ${cfg.cta !== "View" ? cfg.cta : status === "complete" ? "Complete" : "Mango reviewing"}`;
      // Use the human stage label for clarity in a legend context.
      const LABEL: Record<string, string> = {
        offered:         "Offered",
        scheduled:       "Scheduled",
        onsite:          "Onsite now",
        awaiting:        "Processing",
        finalizing:      "FMV approval",
        mango_reviewing: "Mango reviewing",
        complete:        "Complete",
      };
      swatch.textContent = LABEL[status] ?? status;
      swatch.innerHTML = `<i class="ti ${cfg.icon}" style="font-size:10px;margin-right:3px"></i>${LABEL[status] ?? status}`;
      container.appendChild(swatch);
    }

    // Reschedule-pending swatch — uses amber (same as the pending badge on rows).
    const pendingSwatch = document.createElement("span");
    pendingSwatch.setAttribute("data-cal-legend-swatch", "");
    pendingSwatch.className = "pill pill-amber";
    pendingSwatch.style.cssText = "font-size:10px;display:inline-flex;align-items:center;gap:3px";
    pendingSwatch.innerHTML = `<i class="ti ti-clock-edit" style="font-size:10px"></i> Reschedule pending`;
    container.appendChild(pendingSwatch);
  }

  private renderGrid(): void {
    const grid = this.querySelector<HTMLElement>("[data-cal-grid]");
    const cellTpl = this.querySelector<HTMLTemplateElement>("template[data-cal-cell]");
    const chipTpl = this.querySelector<HTMLTemplateElement>("template[data-cal-chip]");
    if (!grid || !cellTpl || !chipTpl) return;

    const label = new Date(this.viewYear, this.viewMonth, 1)
      .toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const monthEl = this.querySelector<HTMLElement>("[data-cal-month]");
    if (monthEl) monthEl.textContent = label;

    const firstWeekday = new Date(this.viewYear, this.viewMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const byDay = this.pickupsByDay();
    const today = new Date();
    const isThisMonth = today.getFullYear() === this.viewYear && today.getMonth() === this.viewMonth;

    grid.replaceChildren();
    // Leading blanks so day 1 lands on its weekday.
    for (let i = 0; i < firstWeekday; i++) {
      const blank = cellTpl.content.cloneNode(true) as DocumentFragment;
      const cell = blank.querySelector<HTMLElement>(".cal-day");
      if (cell) cell.style.background = "var(--surface-2)";
      grid.appendChild(blank);
    }
    let total = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const frag = cellTpl.content.cloneNode(true) as DocumentFragment;
      const cell = frag.querySelector<HTMLElement>(".cal-day");
      const numEl = frag.querySelector<HTMLElement>("[data-cal-daynum]");
      const chips = frag.querySelector<HTMLElement>("[data-cal-chips]");
      if (numEl) numEl.textContent = String(day);
      if (cell && isThisMonth && today.getDate() === day) cell.classList.add("today");
      for (const job of byDay.get(day) ?? []) {
        total++;
        const chipFrag = chipTpl.content.cloneNode(true) as DocumentFragment;
        const pill = chipFrag.querySelector<HTMLElement>("[data-cal-chip-pill]");
        if (pill) {
          const cfg: StatusVisual | undefined = job.provider_status ? STATUS_CONFIG[job.provider_status] : undefined;
          pill.classList.add(cfg?.pillClass ?? "pill-mono");
          // Append a reschedule-pending suffix when the field is set.
          const isPending = Boolean(job.reschedule_requested_for);
          pill.textContent = isPending ? `${job.short_name} ↻` : job.short_name;
          pill.dataset.id = job.short_name;
          pill.dataset.route = detailRoute(job);
          const pendingNote = isPending ? " — Reschedule pending" : "";
          pill.title = `${job.short_name} — ${job.provider_status_display ?? ""}${pendingNote}`;
          if (isPending) {
            pill.style.outline = "1px solid var(--amber)";
            pill.style.outlineOffset = "1px";
          }
        }
        chips?.appendChild(chipFrag);
      }
      grid.appendChild(frag);
    }
    this.setStatus(total === 0 ? "No pickups scheduled this month." : "");
  }

  /**
   * Render the agenda list from upcoming orders (today-or-future, chronological).
   * Each row carries data-id + data-route so the shared keydown/click handler
   * navigates identically to a calendar chip.
   */
  private renderAgenda(): void {
    const container = this.querySelector<HTMLElement>("[data-cal-agenda]");
    const rowTpl = this.querySelector<HTMLTemplateElement>("template[data-cal-agenda-row]");
    if (!container || !rowTpl) return;

    container.replaceChildren();

    const upcoming = this.upcomingOrders();

    if (upcoming.length === 0) {
      const empty = document.createElement("p");
      empty.className = "cal-agenda-empty";
      empty.textContent = "No upcoming pickups scheduled.";
      container.appendChild(empty);
      return;
    }

    let currentMonthKey = "";
    for (const job of upcoming) {
      const parsed = this.parseIsoDate(job);

      // Month group header — inserted whenever the month changes, so the agenda
      // reads as grouped sections (June 2026 · July 2026 …) in one scroll instead
      // of a flat list that needs prev/next month paging.
      if (parsed) {
        const monthKey = `${parsed.y}-${String(parsed.m).padStart(2, "0")}`;
        if (monthKey !== currentMonthKey) {
          currentMonthKey = monthKey;
          const hdr = document.createElement("div");
          hdr.className = "cal-agenda-month";
          hdr.textContent = new Date(parsed.y, parsed.m - 1, 1)
            .toLocaleDateString("en-US", { month: "long", year: "numeric" });
          container.appendChild(hdr);
        }
      }

      const frag = rowTpl.content.cloneNode(true) as DocumentFragment;
      const row = frag.querySelector<HTMLElement>(".cal-agenda-row");
      const dateEl = frag.querySelector<HTMLElement>("[data-cal-agenda-date]");
      const idEl = frag.querySelector<HTMLElement>("[data-cal-agenda-id]");
      const regionEl = frag.querySelector<HTMLElement>("[data-cal-agenda-region]");
      const statusEl = frag.querySelector<HTMLElement>("[data-cal-agenda-status]");

      if (!row) continue;

      const cfg: StatusVisual | undefined = job.provider_status
        ? STATUS_CONFIG[job.provider_status]
        : undefined;

      // Stamp navigation data (same pattern as grid chips).
      row.dataset.id = job.short_name;
      row.dataset.route = detailRoute(job);
      row.setAttribute(
        "aria-label",
        `${job.short_name} — ${job.provider_status_display ?? ""} — ${date(job.dropoff_datetime)}`,
      );

      // Status accent on the left border via a CSS class.
      if (job.provider_status) {
        row.dataset.status = job.provider_status;
      }

      // Date column: formatted from dropoff_datetime (honest data only; reuse parsed).
      if (dateEl) {
        if (parsed) {
          // Two-line: abbreviated month + day number (e.g. "Jun" / "12").
          const dt = new Date(parsed.y, parsed.m - 1, parsed.d);
          const mon = dt.toLocaleDateString("en-US", { month: "short" });
          const dayNum = String(parsed.d);
          dateEl.innerHTML = `<span class="cal-agenda-date-mon">${mon}</span><span class="cal-agenda-date-day">${dayNum}</span>`;
        } else {
          dateEl.textContent = "—";
        }
      }

      // Work-order code.
      if (idEl) {
        idEl.textContent = job.short_name ?? "—";
      }

      // Pickup location — pickup_region is a real contract field (also on the job
      // cards). Hidden when absent so the row never shows an empty line.
      if (regionEl) {
        const region = job.pickup_region ?? "";
        regionEl.textContent = region;
        regionEl.hidden = region === "";
      }

      // Status label with a colour pill matching the grid chips.
      if (statusEl) {
        statusEl.textContent = job.provider_status_display ?? job.provider_status ?? "—";
        statusEl.className = `cal-agenda-status pill ${cfg?.pillClass ?? "pill-mono"}`;
      }

      // Reschedule-pending badge — shown when reschedule_requested_for is set.
      const pendingBadge = frag.querySelector<HTMLElement>("[data-cal-agenda-pending]");
      if (pendingBadge && job.reschedule_requested_for) {
        pendingBadge.style.display = "";
      }

      container.appendChild(frag);
    }
  }

  // ── Reschedule modal ───────────────────────────────────────────────────────
  /**
   * Wire the "Reschedule pickup" button, the modal close/cancel controls, the
   * work-order selector (updates the currently-scheduled line on change), and
   * the "Send change request" submit button.
   *
   * The modal is authored in calendar.html as .modal-overlay[data-reschedule-modal];
   * it follows the same pattern as the quick-accept modal in jobs.html.
   */
  private wireReschedule(): void {
    const overlay = this.querySelector<HTMLElement>("[data-reschedule-modal]");
    if (!overlay) return;

    // Open.
    this.querySelector<HTMLButtonElement>("[data-reschedule-open]")?.addEventListener("click", () => {
      this.openRescheduleModal(null);
    });

    // Close on Cancel button or clicking the overlay backdrop.
    this.querySelector<HTMLButtonElement>("[data-rs-cancel]")?.addEventListener("click", () => {
      this.closeRescheduleModal();
    });
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeRescheduleModal();
    });

    // Update the "Currently scheduled" line whenever the selected work order changes.
    const select = this.querySelector<HTMLSelectElement>("[data-rs-select]");
    select?.addEventListener("change", () => {
      this.updateCurrentlyScheduled();
    });

    // Submit: build the ISO datetime, POST, reflect pending state.
    this.querySelector<HTMLButtonElement>("[data-rs-submit]")?.addEventListener("click", () => {
      void this.submitReschedule();
    });
  }

  /**
   * Open the reschedule modal, optionally pre-targeting a specific work order by
   * short_name. Populates the <select> from this.orders (upcoming pickups only),
   * resets the date/time inputs to sensible defaults, and updates the current-date line.
   */
  private openRescheduleModal(preselect: string | null): void {
    const overlay = this.querySelector<HTMLElement>("[data-reschedule-modal]");
    const select = this.querySelector<HTMLSelectElement>("[data-rs-select]");
    const dateInput = this.querySelector<HTMLInputElement>("[data-rs-date]");
    const timeInput = this.querySelector<HTMLInputElement>("[data-rs-time]");
    const errorEl = this.querySelector<HTMLElement>("[data-rs-error]");
    if (!overlay || !select) return;

    // Populate the selector with UPCOMING pickups ONLY (today-or-future, sorted
    // ascending) — a past pickup can't be rescheduled (Jordan). upcomingOrders()
    // already filters to dropoff_datetime >= today.
    const now = new Date();
    const upcoming = this.upcomingOrders();

    select.replaceChildren();
    for (const job of upcoming) {
      if (!job.dropoff_datetime) continue;
      const opt = document.createElement("option");
      opt.value = job.short_name;
      const region = job.pickup_region ? ` — ${job.pickup_region}` : "";
      opt.textContent = `${job.short_name}${region}`;
      if (preselect && job.short_name === preselect) opt.selected = true;
      select.appendChild(opt);
    }

    // Default the new date to tomorrow (a simple safe default; the user will change it).
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
    if (dateInput) dateInput.value = tomorrowStr;
    if (timeInput && !timeInput.value) timeInput.value = "09:00";

    // Clear error.
    if (errorEl) { errorEl.textContent = ""; errorEl.style.display = "none"; }

    this.updateCurrentlyScheduled();
    overlay.classList.add("show");
    select.focus();
  }

  private closeRescheduleModal(): void {
    this.querySelector<HTMLElement>("[data-reschedule-modal]")?.classList.remove("show");
  }

  /**
   * Refresh the "Currently scheduled: <date>" line from the selected work order.
   * Called on modal open and on <select> change.
   */
  private updateCurrentlyScheduled(): void {
    const select = this.querySelector<HTMLSelectElement>("[data-rs-select]");
    const currentEl = this.querySelector<HTMLElement>("[data-rs-current]");
    if (!select || !currentEl) return;
    const job = this.orders.find((j) => j.short_name === select.value);
    if (!job?.dropoff_datetime) {
      currentEl.textContent = "—";
      return;
    }
    // Format date + time from the dropoff_datetime ISO string.
    const d = new Date(job.dropoff_datetime);
    if (Number.isNaN(d.getTime())) {
      currentEl.textContent = date(job.dropoff_datetime);
      return;
    }
    const formattedDate = d.toLocaleDateString("en-US", {
      weekday: "short", year: "numeric", month: "short", day: "numeric",
    });
    const formattedTime = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    currentEl.textContent = `${formattedDate} · ${formattedTime}`;
  }

  /**
   * Build the `requested_for` ISO 8601 datetime from the date + time inputs.
   *
   * The date input gives "YYYY-MM-DD" and the time input gives "HH:MM". We combine
   * them as a LOCAL datetime by using `new Date(year, month-1, day, hour, minute)` —
   * this is the wall-clock time the provider chose, in their local timezone.
   * `toISOString()` would give UTC midnight (wrong); `toLocaleString` is locale-
   * dependent. Instead we format the ISO string from the local Date parts directly,
   * e.g. "2026-06-25T13:00:00" — no trailing Z so the server treats it as-given.
   */
  private buildRequestedFor(dateVal: string, timeVal: string): string | null {
    if (!dateVal || !timeVal) return null;
    const dateParts = dateVal.split("-");
    const timeParts = timeVal.split(":");
    if (dateParts.length < 3 || timeParts.length < 2) return null;
    const y = Number.parseInt(dateParts[0] ?? "", 10);
    const mo = Number.parseInt(dateParts[1] ?? "", 10);
    const d = Number.parseInt(dateParts[2] ?? "", 10);
    const h = Number.parseInt(timeParts[0] ?? "", 10);
    const mi = Number.parseInt(timeParts[1] ?? "", 10);
    if ([y, mo, d, h, mi].some(Number.isNaN)) return null;
    // Zero-pad each component to produce a clean ISO-like local datetime string.
    const pad = (n: number): string => String(n).padStart(2, "0");
    return `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:00`;
  }

  /**
   * POST the reschedule request: { requested_for: "<ISO local datetime>" }.
   * On success: update the matching order in this.orders with the returned
   * reschedule_requested_for value and re-render so the pending indicator appears.
   */
  private async submitReschedule(): Promise<void> {
    const select = this.querySelector<HTMLSelectElement>("[data-rs-select]");
    const dateInput = this.querySelector<HTMLInputElement>("[data-rs-date]");
    const timeInput = this.querySelector<HTMLInputElement>("[data-rs-time]");
    const submitBtn = this.querySelector<HTMLButtonElement>("[data-rs-submit]");
    const errorEl = this.querySelector<HTMLElement>("[data-rs-error]");

    const shortName = select?.value ?? "";
    const dateVal = dateInput?.value ?? "";
    const timeVal = timeInput?.value ?? "";

    if (!shortName) {
      if (errorEl) { errorEl.textContent = "Please select a work order."; errorEl.style.display = ""; }
      return;
    }
    const requestedFor = this.buildRequestedFor(dateVal, timeVal);
    if (!requestedFor) {
      if (errorEl) { errorEl.textContent = "Please enter a valid date and time."; errorEl.style.display = ""; }
      return;
    }

    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = "Sending…"; }
    if (errorEl) { errorEl.style.display = "none"; }

    try {
      const updated = await request<WorkOrder>(
        `/api/v1/provider/work-orders/${encodeURIComponent(shortName)}/reschedule/`,
        { method: "POST", json: { requested_for: requestedFor } },
      );
      // Reflect the returned reschedule_requested_for on the local copy so the
      // pending indicator shows without a full reload.
      const idx = this.orders.findIndex((j) => j.short_name === shortName);
      if (idx !== -1) {
        // Merge the updated reschedule_requested_for into the cached order.
        this.orders[idx] = { ...this.orders[idx], ...updated };
      }
      this.closeRescheduleModal();
      this.render();
    } catch (err) {
      console.error("[mango-calendar] reschedule failed", err);
      if (errorEl) {
        errorEl.textContent = "Could not send the change request. Please retry.";
        errorEl.style.display = "";
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="ti ti-send" style="font-size:12px"></i> Send change request to client';
      }
    }
  }
}

export function registerMangoCalendar(): void {
  if (!customElements.get("mango-calendar")) {
    customElements.define("mango-calendar", MangoCalendar);
  }
}
